export enum DEFAULT_TRACK_ACTION {
  // Only show if you are a Collaborator+
  DUPLICATE = "duplicate",
  // Always show
  DETAILS = "details",
  // Only show if you are a Collaborator+
  MOVE_TO_SPACE = "move-to-space",
  // Only show if you are the Owner
  DELETE = "delete",
  // Only show if you are a Space owner/manager but NOT the Track's creator.
  // Removing (vs. deleting) returns the Track to its creator's drafts.
  REMOVE_FROM_SPACE = "remove-from-space",
  // Not an action, but a way to include a separator in the actions menus
  SEPARATOR = "separator",
  // Only show if you are Collaborator+ of the Space OR Collaborator+ of the Track
  DOWNLOAD_DEFAULT_VERSION = "download-default-version",
  DOWNLOAD_ALL_VERSIONS = "download-all-versions",
  // Only show if you are the Owner
  MANAGE_VERSIONS = "manage-versions",
}

// getDefaultTrackActions computes which actions appear in a single Track's
// menu based on the requester's relationship to the Track and its Space.
// - The creator can delete (hard-delete) their own Track.
// - A Space owner/manager who isn't the creator can instead *remove* it,
//   returning the Track to the creator's drafts. The two are mutually
//   exclusive: the creator never sees "Remove from Space".
export const getDefaultTrackActions = ({
  isTrackOwner,
  canManageSpace,
}: {
  isTrackOwner: boolean;
  canManageSpace: boolean;
}): DEFAULT_TRACK_ACTION[] => {
  return [
    DEFAULT_TRACK_ACTION.DETAILS,

    ...(canManageSpace || isTrackOwner
      ? [
          DEFAULT_TRACK_ACTION.SEPARATOR,
          DEFAULT_TRACK_ACTION.MANAGE_VERSIONS,
          DEFAULT_TRACK_ACTION.DOWNLOAD_DEFAULT_VERSION,
          DEFAULT_TRACK_ACTION.DOWNLOAD_ALL_VERSIONS,
          DEFAULT_TRACK_ACTION.SEPARATOR,
        ]
      : []),

    ...(canManageSpace ? [DEFAULT_TRACK_ACTION.MOVE_TO_SPACE] : []),

    ...(canManageSpace || isTrackOwner ? [DEFAULT_TRACK_ACTION.DUPLICATE] : []),

    ...(isTrackOwner
      ? [DEFAULT_TRACK_ACTION.SEPARATOR, DEFAULT_TRACK_ACTION.DELETE]
      : canManageSpace
        ? [
            DEFAULT_TRACK_ACTION.SEPARATOR,
            DEFAULT_TRACK_ACTION.REMOVE_FROM_SPACE,
          ]
        : []),
  ];
};

/**
 * The server refuses a Delete/Remove issued from a space that only BORROWS
 * the track (`assertNotDeletingFromBorrowingSpace`), with this `reason` in
 * the HttpsError details. It is not a transient failure — "please try
 * again" is wrong twice: it does not say why, and a retry cannot succeed.
 */
export const USE_UNSHARE_REASON = "USE_UNSHARE";

export const isUseUnshareError = (error: unknown): boolean =>
  (error as { details?: { reason?: string } } | undefined)?.details?.reason ===
  USE_UNSHARE_REASON;

export const USE_UNSHARE_MESSAGE =
  "This track belongs to another space. Remove it from this space instead.";

export const getDeleteTrackErrorMessage = (
  error: unknown,
  isRemove: boolean,
): string => {
  if (isUseUnshareError(error)) return USE_UNSHARE_MESSAGE;
  return isRemove
    ? "Could not remove Track. Please try again."
    : "Could not delete Track. Please try again.";
};
